import React, { useState, useEffect, useCallback } from 'react';
import { 
  FiBookOpen, 
  FiFilter, 
  FiPlus, 
  FiEdit, 
  FiTrash2, 
  FiEye, 
  FiUpload, 
  FiDownload,
  FiCheckCircle,
  FiXCircle,
  FiClock,
  FiMoreVertical,
  FiTag,
  
} from 'react-icons/fi';
import Button from '../../components/common/Button';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import Badge from '../../components/common/Badge';
import SearchBar from '../../components/common/SearchBar';
import Pagination from '../../components/common/Pagination';
import { adminAPI } from '../../services/api';
import { useNotification } from '../../contexts/NotificationContext';

const ContentManagement = () => {
  const [content, setContent] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filters, setFilters] = useState({
    type: 'all',
    status: 'all',
    subject: 'all',
    grade: 'all',
    difficulty: 'all'
  });
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 20,
    total: 0
  });
  const [selectedContent, setSelectedContent] = useState([]);
  const [showFilters, setShowFilters] = useState(false);
  const [actionMenuOpen, setActionMenuOpen] = useState(null);
  const { showError, showSuccess } = useNotification();

  const contentTypes = [
    { value: 'question', label: 'Questions' },
    { value: 'lesson', label: 'Lessons' },
    { value: 'game', label: 'Games' },
    { value: 'assessment', label: 'Assessments' }
  ];

  const statuses = [
    { value: 'draft', label: 'Draft' },
    { value: 'pending', label: 'Pending Review' },
    { value: 'approved', label: 'Approved' },
    { value: 'published', label: 'Published' },
    { value: 'rejected', label: 'Rejected' },
    { value: 'archived', label: 'Archived' }
  ];

  const subjects = [
    'Mathematics', 'Science', 'English', 'History', 'Geography',
    'Physics', 'Chemistry', 'Biology', 'Computer Science'
  ];

  const grades = [
    'Grade 1', 'Grade 2', 'Grade 3', 'Grade 4', 'Grade 5',
    'Grade 6', 'Grade 7', 'Grade 8', 'Grade 9', 'Grade 10'
  ];

  const difficulties = [
    { value: 'easy', label: 'Easy' },
    { value: 'medium', label: 'Medium' },
    { value: 'hard', label: 'Hard' }
  ];

  const fetchContent = useCallback(async () => {
    try {
      setLoading(true);
      const response = await adminAPI.getContent({
        search: searchTerm,
        ...filters,
        page: pagination.page,
        limit: pagination.limit
      });
      setContent(response.data.content);
      setPagination(prev => ({
        ...prev,
        total: response.data.total
      }));
    } catch (error) {
      showError('Failed to load content');
    } finally {
      setLoading(false);
    }
  }, [searchTerm, filters, pagination.page, pagination.limit, showError]);

  useEffect(() => {
    fetchContent();
  }, [fetchContent]);

  const handleSearch = (term) => {
    setSearchTerm(term);
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  const handleContentSelect = (contentId) => {
    setSelectedContent(prev => 
      prev.includes(contentId) 
        ? prev.filter(id => id !== contentId)
        : [...prev, contentId]
    );
  };

  const handleSelectAll = () => {
    if (selectedContent.length === content.length) {
      setSelectedContent([]);
    } else {
      setSelectedContent(content.map(item => item.id));
    }
  };

  const handleContentAction = async (action, contentId) => {
    try {
      switch (action) {
        case 'approve':
          await adminAPI.updateContentStatus(contentId, 'approved');
          showSuccess('Content approved successfully');
          break;
        case 'reject':
          await adminAPI.updateContentStatus(contentId, 'rejected');
          showSuccess('Content rejected successfully');
          break;
        case 'publish':
          await adminAPI.updateContentStatus(contentId, 'published');
          showSuccess('Content published successfully');
          break;
        case 'archive':
          await adminAPI.updateContentStatus(contentId, 'archived');
          showSuccess('Content archived successfully');
          break;
        case 'delete':
          if (window.confirm('Are you sure you want to delete this content?')) {
            await adminAPI.deleteContent(contentId);
            showSuccess('Content deleted successfully');
          }
          break;
        default:
          break;
      }
      fetchContent();
    } catch (error) {
      showError(`Failed to ${action} content`);
    }
    setActionMenuOpen(null);
  };

  const handleBulkAction = async (action) => {
    if (selectedContent.length === 0) {
      showError('Please select content first');
      return;
    }

    try {
      switch (action) {
        case 'approve':
          await adminAPI.bulkUpdateContent(selectedContent, { status: 'approved' });
          showSuccess(`${selectedContent.length} items approved`);
          break;
        case 'publish':
          await adminAPI.bulkUpdateContent(selectedContent, { status: 'published' });
          showSuccess(`${selectedContent.length} items published`);
          break;
        case 'archive':
          await adminAPI.bulkUpdateContent(selectedContent, { status: 'archived' });
          showSuccess(`${selectedContent.length} items archived`);
          break;
        case 'delete':
          if (window.confirm(`Are you sure you want to delete ${selectedContent.length} items?`)) {
            await adminAPI.bulkDeleteContent(selectedContent);
            showSuccess(`${selectedContent.length} items deleted`);
          }
          break;
        default:
          break;
      }
      setSelectedContent([]);
      fetchContent();
    } catch (error) {
      showError(`Failed to ${action} content`);
    }
  };

  const getStatusBadge = (status) => {
    const statusMap = {
      draft: { variant: 'secondary', label: 'Draft' },
      pending: { variant: 'warning', label: 'Pending' },
      approved: { variant: 'success', label: 'Approved' },
      published: { variant: 'primary', label: 'Published' },
      rejected: { variant: 'danger', label: 'Rejected' },
      archived: { variant: 'info', label: 'Archived' }
    };
    
    const config = statusMap[status] || { variant: 'secondary', label: status };
    return <Badge variant={config.variant}>{config.label}</Badge>;
  };

  const getTypeBadge = (type) => {
    const typeMap = {
      question: { variant: 'primary', label: 'Question' },
      lesson: { variant: 'success', label: 'Lesson' },
      game: { variant: 'info', label: 'Game' },
      assessment: { variant: 'warning', label: 'Assessment' }
    };
    
    const config = typeMap[type] || { variant: 'secondary', label: type };
    return <Badge variant={config.variant}>{config.label}</Badge>;
  };

  const getDifficultyBadge = (difficulty) => {
    const difficultyMap = {
      easy: { variant: 'success', label: 'Easy' },
      medium: { variant: 'warning', label: 'Medium' },
      hard: { variant: 'danger', label: 'Hard' }
    };
    
    const config = difficultyMap[difficulty] || { variant: 'secondary', label: difficulty };
    return <Badge variant={config.variant} size="small">{config.label}</Badge>;
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  if (loading && content.length === 0) {
    return (
      <div className="page-container">
        <LoadingSpinner text="Loading content..." />
      </div>
    );
  }

  return (
    <div className="page-container">
      <div className="page-header">
        <div>
          <h1 className="page-title">Content Management</h1>
          <p className="page-description">Manage educational content and resources</p>
        </div>
        <div className="header-actions">
          <Button variant="outline">
            <FiDownload size={16} />
            Export
          </Button>
          <Button variant="outline">
            <FiUpload size={16} />
            Import
          </Button>
          <Button variant="primary">
            <FiPlus size={16} />
            Add Content
          </Button>
        </div>
      </div>

      <div className="page-content">
        {/* Search and Filters */}
        <div className="search-filters-section">
          <div className="search-bar-container">
            <SearchBar
              placeholder="Search content by title, description, or tags..."
              onSearch={handleSearch}
              value={searchTerm}
            />
          </div>
          
          <div className="filters-container">
            <Button 
              variant="outline" 
              onClick={() => setShowFilters(!showFilters)}
              className={showFilters ? 'active' : ''}
            >
              <FiFilter size={16} />
              Filters
            </Button>
            
            {showFilters && (
              <div className="filters-panel">
                <div className="filter-group">
                  <label>Content Type</label>
                  <select
                    value={filters.type}
                    onChange={(e) => handleFilterChange('type', e.target.value)}
                    className="form-select"
                  >
                    <option value="all">All Types</option>
                    {contentTypes.map(type => (
                      <option key={type.value} value={type.value}>{type.label}</option>
                    ))}
                  </select>
                </div>
                
                <div className="filter-group">
                  <label>Status</label>
                  <select
                    value={filters.status}
                    onChange={(e) => handleFilterChange('status', e.target.value)}
                    className="form-select"
                  >
                    <option value="all">All Statuses</option>
                    {statuses.map(status => (
                      <option key={status.value} value={status.value}>{status.label}</option>
                    ))}
                  </select>
                </div>
                
                <div className="filter-group">
                  <label>Subject</label>
                  <select
                    value={filters.subject}
                    onChange={(e) => handleFilterChange('subject', e.target.value)}
                    className="form-select"
                  >
                    <option value="all">All Subjects</option>
                    {subjects.map(subject => (
                      <option key={subject} value={subject}>{subject}</option>
                    ))}
                  </select>
                </div>
                
                <div className="filter-group">
                  <label>Grade</label>
                  <select
                    value={filters.grade}
                    onChange={(e) => handleFilterChange('grade', e.target.value)}
                    className="form-select"
                  >
                    <option value="all">All Grades</option>
                    {grades.map(grade => (
                      <option key={grade} value={grade}>{grade}</option>
                    ))}
                  </select>
                </div>
                
                <div className="filter-group">
                  <label>Difficulty</label>
                  <select
                    value={filters.difficulty}
                    onChange={(e) => handleFilterChange('difficulty', e.target.value)}
                    className="form-select"
                  >
                    <option value="all">All Difficulties</option>
                    {difficulties.map(difficulty => (
                      <option key={difficulty.value} value={difficulty.value}>{difficulty.label}</option>
                    ))}
                  </select>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Bulk Actions */}
        {selectedContent.length > 0 && (
          <div className="bulk-actions">
            <div className="bulk-info">
              <span>{selectedContent.length} item(s) selected</span>
            </div>
            <div className="bulk-buttons">
              <Button 
                variant="outline" 
                size="small"
                onClick={() => handleBulkAction('approve')}
              >
                <FiCheckCircle size={14} />
                Approve
              </Button>
              <Button 
                variant="outline" 
                size="small"
                onClick={() => handleBulkAction('publish')}
              >
                <FiBookOpen size={14} />
                Publish
              </Button>
              <Button 
                variant="outline" 
                size="small"
                onClick={() => handleBulkAction('archive')}
              >
                <FiClock size={14} />
                Archive
              </Button>
              <Button 
                variant="danger" 
                size="small"
                onClick={() => handleBulkAction('delete')}
              >
                <FiTrash2 size={14} />
                Delete
              </Button>
            </div>
          </div>
        )}

        {/* Content Table */}
        <div className="content-table">
          <div className="table-header">
            <div className="header-cell checkbox-cell">
              <input
                type="checkbox"
                checked={selectedContent.length === content.length && content.length > 0}
                onChange={handleSelectAll}
              />
            </div>
            <div className="header-cell">Content</div>
            <div className="header-cell">Type</div>
            <div className="header-cell">Status</div>
            <div className="header-cell">Subject</div>
            <div className="header-cell">Author</div>
            <div className="header-cell">Created</div>
            <div className="header-cell">Actions</div>
          </div>
          
          {loading ? (
            <div className="table-loading">
              <LoadingSpinner size="small" />
            </div>
          ) : (
            <div className="table-body">
              {content.map((item) => (
                <div key={item.id} className="table-row">
                  <div className="table-cell checkbox-cell">
                    <input
                      type="checkbox"
                      checked={selectedContent.includes(item.id)}
                      onChange={() => handleContentSelect(item.id)}
                    />
                  </div>
                  
                  <div className="table-cell content-cell">
                    <div className="content-info">
                      <div className="content-thumbnail">
                        {item.thumbnail ? (
                          <img src={item.thumbnail} alt={item.title} />
                        ) : (
                          <div className="thumbnail-placeholder">
                            <FiBookOpen size={20} />
                          </div>
                        )}
                      </div>
                      <div className="content-details">
                        <h4 className="content-title">{item.title}</h4>
                        <p className="content-description">{item.description}</p>
                        <div className="content-meta">
                          <span className="meta-item">
                            <FiTag size={12} />
                            {item.grade}
                          </span>
                          {item.difficulty && (
                            <span className="meta-item">
                              {getDifficultyBadge(item.difficulty)}
                            </span>
                          )}
                          {item.tags && item.tags.length > 0 && (
                            <span className="meta-item">
                              {item.tags.slice(0, 2).join(', ')}
                              {item.tags.length > 2 && ` +${item.tags.length - 2}`}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  <div className="table-cell">
                    {getTypeBadge(item.type)}
                  </div>
                  
                  <div className="table-cell">
                    {getStatusBadge(item.status)}
                  </div>
                  
                  <div className="table-cell">
                    <span className="subject-name">{item.subject}</span>
                  </div>
                  
                  <div className="table-cell">
                    <div className="author-info">
                      <span className="author-name">{item.author?.name}</span>
                      <span className="author-role">{item.author?.role}</span>
                    </div>
                  </div>
                  
                  <div className="table-cell">
                    <div className="date-info">
                      <span className="created-date">{formatDate(item.createdAt)}</span>
                      {item.updatedAt !== item.createdAt && (
                        <span className="updated-date">Updated {formatDate(item.updatedAt)}</span>
                      )}
                    </div>
                  </div>
                  
                  <div className="table-cell actions-cell">
                    <div className="action-buttons">
                      <Button variant="text" size="small" title="Preview">
                        <FiEye size={14} />
                      </Button>
                      <Button variant="text" size="small" title="Edit">
                        <FiEdit size={14} />
                      </Button>
                      
                      <div className="action-menu">
                        <Button 
                          variant="text" 
                          size="small"
                          onClick={() => setActionMenuOpen(actionMenuOpen === item.id ? null : item.id)}
                        >
                          <FiMoreVertical size={14} />
                        </Button>
                        
                        {actionMenuOpen === item.id && (
                          <div className="action-dropdown">
                            {item.status === 'pending' && (
                              <>
                                <button onClick={() => handleContentAction('approve', item.id)}>
                                  <FiCheckCircle size={14} />
                                  Approve
                                </button>
                                <button onClick={() => handleContentAction('reject', item.id)}>
                                  <FiXCircle size={14} />
                                  Reject
                                </button>
                              </>
                            )}
                            {item.status === 'approved' && (
                              <button onClick={() => handleContentAction('publish', item.id)}>
                                <FiBookOpen size={14} />
                                Publish
                              </button>
                            )}
                            {(item.status === 'published' || item.status === 'approved') && (
                              <button onClick={() => handleContentAction('archive', item.id)}>
                                <FiClock size={14} />
                                Archive
                              </button>
                            )}
                            <button 
                              onClick={() => handleContentAction('delete', item.id)}
                              className="danger"
                            >
                              <FiTrash2 size={14} />
                              Delete
                            </button>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Pagination */}
        <Pagination
          currentPage={pagination.page}
          totalPages={Math.ceil(pagination.total / pagination.limit)}
          onPageChange={(page) => setPagination(prev => ({ ...prev, page }))}
          totalItems={pagination.total}
          itemsPerPage={pagination.limit}
        />
      </div>
    </div>
  );
};

export default ContentManagement;